import type { HudCharacterData, CharacterHudHelpers } from '../../lib/character-hud-store';
import type { DragHandleProps } from './types';
import { useI18n } from '../../i18n';
import { hudDeathStatus } from './death-state';

interface PillStateProps {
  data: HudCharacterData;
  helpers: CharacterHudHelpers;
  onExpand: () => void;
  onOpenFullSheet: () => void;
  dragHandleProps: DragHandleProps;
}

export function PillState({ data, helpers: _helpers, onExpand, onOpenFullSheet, dragHandleProps }: PillStateProps) {
  const { t } = useI18n();
  const hpPercent = data.hp.max > 0 ? data.hp.current / data.hp.max : 1;
  const isLowHp = hpPercent < 0.35;
  const hasConcentration = data.spells.concentrationSpellId !== null;
  const deathStatus = hudDeathStatus(data);

  return (
    <div
      data-testid="hud-pill"
      className="inline-flex items-center gap-2 cursor-pointer select-none"
      style={{
        backgroundColor: 'var(--paper-bg)',
        border: '1px solid var(--paper-rule)',
        borderRadius: 4,
        boxShadow:
          '0 1px 0 rgba(0,0,0,.04) inset, 0 14px 32px -12px rgba(0,0,0,.55), 0 4px 10px -2px rgba(0,0,0,.35)',
        minWidth: 200,
        maxWidth: 320,
        height: 34,
        padding: '6px 10px',
      }}
      onClick={onExpand}
      {...dragHandleProps}
      role="button"
      tabIndex={0}
      aria-label={t('characterHud.expand')}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onExpand(); }}
    >
      {/* Character name — the platform font like the rest of the table (owner pass: the one serif plate was an outlier) */}
      <span
        className="truncate font-semibold text-body"
        style={{ maxWidth: 110, flexShrink: 0, color: 'var(--paper-ink)' }}
      >
        {data.identity.name}
      </span>

      {/* HP — JetBrains Mono, current / max with muted max */}
      <span
        className="paper-mono flex items-baseline gap-1 whitespace-nowrap text-body-s"
        style={{ color: isLowHp ? 'var(--paper-bordo)' : 'var(--paper-ink)' }}
      >
        <span>{data.hp.current}</span>
        <span className="text-pill" style={{ color: 'var(--paper-ink-2)', fontWeight: 400 }}>
          / {data.hp.max}
        </span>
      </span>

      {/* Death badge — dying: bordo skull + save tally; dead/stable: skull */}
      {deathStatus !== 'alive' && (
        <span
          className="paper-mono whitespace-nowrap text-label"
          data-testid="pill-death-badge"
          data-death-status={deathStatus}
          style={{
            fontWeight: 700,
            color: deathStatus === 'stable' ? 'var(--paper-ink-2)' : 'var(--paper-bordo)',
            flexShrink: 0,
          }}
          aria-label={
            deathStatus === 'dead' ? t('characterHud.deathDead')
            : deathStatus === 'dying' ? t('characterHud.deathDying')
            : t('characterHud.deathStable')
          }
          title={
            deathStatus === 'dead' ? t('characterHud.deathDead')
            : deathStatus === 'dying' ? t('characterHud.deathDying')
            : t('characterHud.deathStable')
          }
        >
          ☠{deathStatus === 'dying' ? ` ✓${data.deathSaves.successes} ✗${data.deathSaves.failures}` : ''}
        </span>
      )}

      {/* Concentration badge — gold circle with К */}
      {hasConcentration && (
        <span
          className="flex items-center justify-center text-label"
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: 'var(--paper-gold)',
            border: '1px solid var(--paper-gold)',
            color: 'var(--paper-bg)',
            fontFamily: 'Fraunces, serif',
            fontWeight: 700,
            fontStyle: 'italic',
            flexShrink: 0,
          }}
          aria-label={t('characterHud.tileConc')}
        >
          К
        </span>
      )}

      {/* Full sheet button */}
      <button
        className="hud-target ml-auto opacity-60 hover:opacity-100 text-body"
        style={{ color: 'var(--paper-ink-2)', minWidth: 24, minHeight: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        onClick={(e) => {
          e.stopPropagation();
          onOpenFullSheet();
        }}
        aria-label={t('characterHud.openFullSheet')}
        title={t('characterHud.openFullSheet')}
      >
        ⛶
      </button>
    </div>
  );
}
